import { Alert, Button, Divider, Form, Input, Modal, Space, Tabs } from 'antd'
import { trimFields } from '../../../utils'

import PropTypes from 'prop-types'
import { useState } from 'react'
import { Can, Icon } from '../../../components'
import { ModifiedModalFooter } from '../../../elements'

import { createStore, updateStore } from '../api'

import { AddressTab, BankDetailsTab, ContactTab, HomeTab, LogisticsTab, OrdersTab } from './tabs'

const errorTabCheck = {
  active: 'home',
  address_line: 'address',
  barcodes: 'logistics',
  city: 'address',
  collection_type: 'logistics',
  comment: 'home',
  contact_name: 'contact',
  contact_phone: 'contact',
  country_id: 'home', // change when country field moved to address tab
  display_name: 'address',
  external_id: 'logistics',
  latitude: 'address',
  longitude: 'address',
  map_details: 'address',
  on_map_from: 'address',
  on_map_until: 'address',
  ownership_model: 'home',
  reporting_email: 'bank',
  retailer_id: 'home',
  return_method: 'logistics',
  reverse_dc_id: 'logistics',
  state: 'address',
  store_format: 'logistics',
  zip_code: 'address',
}

const StoreDetailsModal = ({
  actionButtons,
  countries,
  errorMessage,
  hideModal,
  isModalLoading,
  handleSuccess,
  handleFailure,
  retailers = [],
  selectedStore,
  updateSelectedStore,
  setModalLoading,
}) => {
  const [form] = Form.useForm()
  const [activeTab, setActiveTab] = useState('home')
  const [barcodeErrors, setBarcodeErrors] = useState([])

  const isUpdate = !!selectedStore
  const hasWiseId = isUpdate && !!selectedStore.wise_id

  const resetErrorFields = () => {
    form.getFieldsError().forEach((field) => {
      if (field.errors.length > 0) {
        form.setFields([{ errors: [], name: field.name }])
      }
    })
  }

  const handleSubmit = () => {
    const isFormUpdated = form.isFieldsTouched()

    if (isFormUpdated) {
      form
        .validateFields()
        .then(() => {
          setBarcodeErrors([])
          setModalLoading(true)

          const fieldsValue = trimFields(form.getFieldsValue([
            'active',
            'address_line',
            'city',
            'comment',
            'contact_name',
            'contact_phone',
            'country_id',
            'name',
            'retailer_id',
            'reporting_email',
            'state',
            'zip_code',
            'display_name',
            'latitude',
            'longitude',
            'on_map_from',
            'on_map_until',
            'ownership_model',
            'map_details',
            'return_method',
            'collection_type',
            'store_format',
            'reverse_dc_id',
            'external_id',
          ]))

          if (isUpdate) {

            updateStore({
              ...fieldsValue,
              ...{ barcodes: form.getFieldValue('barcodes_new') },
              ...{ id: selectedStore?.id },
            })
              .then(() => {
                handleSuccess({ description: 'Store updated' }, false)
                resetErrorFields()
              })
              .catch((error) => {
                handleAndShowErrors(error)

                handleFailure(error, 'Store update failed!')
              })
          } else {
            createStore(fieldsValue)
              .then(({ data }) => {
                handleSuccess({ description: 'Store created' }, false)

                updateSelectedStore(data.data)

                form.setFields([
                  {
                    name: 'id',
                    value: data.data.id,
                  },
                  {
                    name: 'tds_store_id',
                    value: data.data.tds_store_id,
                  },
                ])

                resetErrorFields()
              })
              .catch((error) => {
                handleAndShowErrors(error)

                handleFailure(error, 'Store creation failed!')
              })
          }
        })
        .catch((error) => {
          if (errorTabCheck[error.errorFields[0].name[0]]) {
            setActiveTab(errorTabCheck[error.errorFields[0].name[0]])
          }
        })
    } else {
      hideModal()
    }
  }

  const handleAndShowErrors = (error) => {
    const firstErrorName = error?.response?.data?.details
      ? Object.keys(error.response.data.details)[0]
      : ''

    if (errorTabCheck[firstErrorName]) {
      setActiveTab(errorTabCheck[firstErrorName])

      form.setFields([
        {
          errors: [`${firstErrorName} ${error.response?.data?.details[firstErrorName]}`],
          name: firstErrorName,
        },
      ])
    }

    const hasBarcodeError = error?.response?.data?.details?.barcodes

    if (hasBarcodeError) {
      setBarcodeErrors(error.response.data.details.barcodes)

      form.setFields([
        {
          errors: [],
          name: 'barcodes',
        },
      ])
    }

  }

  const initialValues = {
    ...selectedStore,
    active: selectedStore?.active || true,
    balance: selectedStore
      ? new Intl.NumberFormat(selectedStore.country_locales[0], {
        currency: selectedStore.currency,
        style: 'currency',
      }).format(selectedStore?.balance || 0)
      : 0,
    collection_type: selectedStore?.collection_type || null,
    ownership_model: selectedStore?.ownership_model || null,
    return_method: selectedStore?.return_method?.filter(element => element),
    reverse_dc_id: selectedStore?.reverse_dc_id || null,
    tds_store_id: selectedStore?.tds_store_id || null,
  }

  const isViewMode = !initialValues.active

  const tabItems = [
    {
      children: (
        <HomeTab
          countries={countries}
          form={form}
          hasWiseId={hasWiseId}
          initialValues={initialValues}
          isUpdate={isUpdate}
          retailers={retailers}
        />
      ),
      key: 'home',
      label: 'Home',
    },
    {
      children: <AddressTab form={form} initialValues={initialValues} isUpdate={isUpdate} />,
      key: 'address',
      label: 'Address',
    },
    {
      children: <ContactTab />,
      key: 'contact',
      label: 'Contact',
    },
    {
      children: (
        <BankDetailsTab
          form={form}
          handleSuccess={handleSuccess}
          isUpdate={isUpdate}
          selectedStore={selectedStore}
        />
      ),
      forceRender: true,
      key: 'bank',
      label: 'Bank details',
    },
    {
      children: <LogisticsTab barcodeErrors={barcodeErrors} form={form} selectedStore={selectedStore} />,
      forceRender: true,
      key: 'logistics',
      label: 'Logistics',
    },
    {
      children: <OrdersTab selectedStore={selectedStore} />,
      key: 'orders',
      label: 'Orders',
    },
  ]

  return (
    <Modal
      footer={[
        <ModifiedModalFooter key="padding">
          <Space>
            <Button key="back" name="cancelStoreModalBtnSelenium" onClick={hideModal}>
              Cancel
            </Button>

            <Can
              key={isUpdate ? 'update' : 'add'}
              requiredPermission={isUpdate ? 'store.put.store_id' : 'store.post.create'}
              yes={() => (
                <Button
                  data-testid={isUpdate ? 'updateBtn' : 'addBtn'}
                  key="submit"
                  loading={isModalLoading}
                  name={isUpdate ? 'updateDetailsStoreSelenium' : 'addStoreSelenium'}
                  onClick={handleSubmit}
                  type="primary"
                >
                  {isUpdate ? 'Update' : 'Add'}
                </Button>
              )}
            />
          </Space>
        </ModifiedModalFooter>,
      ]}
      onCancel={hideModal}
      onOk={handleSubmit}
      open
      title={isUpdate ? 'Update store' : 'Create store'}
      width="90%"
    >
      <Form data-test="store-details-form" form={form} initialValues={initialValues} layout="vertical">
        <Form.Item
          label="Name"
          name="name"
          rules={[{ message: 'Please provide a name!', required: true }]}
          tooltip={{ icon: <Icon type="infoCircle" />, title: 'Follow standard naming conventions. Visible to corporate users.' }}
        >
          <Input disabled={isViewMode} />
        </Form.Item>

        <Divider />

        <Tabs
          activeKey={activeTab}
          items={tabItems}
          onTabClick={(targetKey) => setActiveTab(targetKey)}
          tabBarExtraContent={actionButtons}
          type="card"
        />
      </Form>

      {errorMessage && (
        <Alert description={errorMessage} showIcon style={{ marginTop: 20 }} title="Error" type="error" />
      )}
    </Modal>
  )
}

StoreDetailsModal.propTypes = {
  actionButtons: PropTypes.element,
  countries: PropTypes.array,
  errorMessage: PropTypes.string,
  handleFailure: PropTypes.func.isRequired,
  handleSuccess: PropTypes.func.isRequired,
  hideModal: PropTypes.func.isRequired,
  isModalLoading: PropTypes.bool,
  retailers: PropTypes.array,
  selectedStore: PropTypes.object,
  setModalLoading: PropTypes.func.isRequired,
  updateSelectedStore: PropTypes.func.isRequired,
}

export default StoreDetailsModal
